/**
 * 활동 로그 컬럼 정의
 * 회사 상세 대시보드의 활동 로그 그리드용
 */

import { CreateColumn } from '../../../../Services/agGrid/createColumn';
import { Box, Typography, Chip, Avatar } from '@mui/material';

const getModuleColor = (module) => {
  const colorMap = {
    '작업 허가서': { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: 'rgba(59, 130, 246, 0.2)' },
    '위험성 평가': { bg: 'rgba(249, 115, 22, 0.1)', color: '#f97316', border: 'rgba(249, 115, 22, 0.2)' },
    'TBM (작업 전 회의)': { bg: 'rgba(168, 85, 247, 0.1)', color: '#a855f7', border: 'rgba(168, 85, 247, 0.2)' },
    '인증': { bg: 'rgba(148, 163, 184, 0.1)', color: '#94a3b8', border: 'rgba(148, 163, 184, 0.2)' },
    '기본': { bg: 'rgba(148, 163, 184, 0.1)', color: '#94a3b8', border: 'rgba(148, 163, 184, 0.2)' },
  };
  return colorMap[module] || colorMap['기본'];
};

const getStatusColor = (status) => {
  const colorMap = {
    '성공': { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: 'rgba(16, 185, 129, 0.2)' },
    '대기 중': { bg: 'rgba(148, 163, 184, 0.1)', color: '#94a3b8', border: 'rgba(148, 163, 184, 0.2)' },
    '실패': { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.2)' },
  };
  return colorMap[status] || colorMap['대기 중'];
};

const getInitials = (name) => {
  if (!name) return '?';
  const words = name.split(' ');
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * 활동 로그 컬럼 정의 생성
 * @returns {Array} 컬럼 정의 배열
 */
export const getActivityLogColumnDefs = () => {
  return [
    // 사용자 컬럼
    CreateColumn(
      'userName',
      '사용자',
      'left',
      200,
      false,
      false,
      false,
      'custom',
      {
        cellRenderer: (params) => {
          const log = params.data;
          if (!log) return null;

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: 12,
                  fontWeight: 700,
                }}
                src={log.photoUrl || log.userAvatar}
              >
                {getInitials(log.userName)}
              </Avatar>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {log.userName || '-'}
              </Typography>
            </Box>
          );
        },
        flex: 1,
        minWidth: 200,
      }
    ),

    // 활동 컬럼
    CreateColumn(
      'activity',
      '활동',
      'left',
      300,
      false,
      false,
      false,
      'text',
      {
        flex: 2,
        minWidth: 300,
        valueGetter: (params) => {
          // activityContent 또는 activity 필드 사용
          return params.data?.activityContent || params.data?.activity || '';
        },
      }
    ),

    // 모듈 컬럼
    CreateColumn(
      'module',
      '모듈',
      'left',
      150,
      false,
      false,
      false,
      'custom',
      {
        cellRenderer: (params) => {
          const log = params.data;
          if (!log) return null;

          const moduleColor = getModuleColor(log.module);

          return (
            <Box sx={{ py: 1 }}>
              <Chip
                label={log.module || '-'}
                size="small"
                sx={{
                  height: 24,
                  fontSize: 11,
                  fontWeight: 500,
                  bgcolor: moduleColor.bg,
                  color: moduleColor.color,
                  border: `1px solid ${moduleColor.border}`,
                }}
              />
            </Box>
          );
        },
        flex: 1,
        minWidth: 150,
      }
    ),

    // 시간 컬럼
    CreateColumn(
      'time',
      '시간',
      'left',
      120,
      false,
      false,
      false,
      'text',
      {
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => {
          // actionDt 또는 time 필드 사용
          const actionDt = params.data?.actionDt || params.data?.time || '';
          if (!actionDt) return '';
          
          // "2025-12-01 16:01:58" 형식을 "2분 전" 형식으로 변환
          try {
            const date = new Date(actionDt);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / (1000 * 60));
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffMins < 1) return '방금 전';
            if (diffMins < 60) return `${diffMins}분 전`;
            if (diffHours < 24) return `${diffHours}시간 전`;
            if (diffDays < 7) return `${diffDays}일 전`;
            
            // 일주일 이상이면 날짜 형식으로 표시
            return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
          } catch (e) {
            return actionDt;
          }
        },
      }
    ),

    // 상태 컬럼
    CreateColumn(
      'status',
      '상태',
      'right',
      120,
      false,
      false,
      false,
      'custom',
      {
        cellRenderer: (params) => {
          const log = params.data;
          if (!log) return null;

          // 상태 변환 (SUCCESS -> 성공, FAIL -> 실패 등)
          const statusText = log.status === 'SUCCESS' ? '성공' : 
                           log.status === 'FAIL' || log.status === 'FAILED' ? '실패' : 
                           log.status === 'PENDING' ? '대기 중' : 
                           log.status || '대기 중';
          const statusColor = getStatusColor(statusText);

          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
              <Chip
                label={statusText || '-'}
                size="small"
                sx={{
                  height: 24,
                  fontSize: 11,
                  fontWeight: 500,
                  bgcolor: statusColor.bg,
                  color: statusColor.color,
                  border: `1px solid ${statusColor.border}`,
                }}
              />
            </Box>
          );
        },
        flex: 1,
        minWidth: 120,
      }
    ),
  ];
};

const activityLogColumnDefs = {
  getActivityLogColumnDefs,
};

export default activityLogColumnDefs;

